import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { ISurfaceAssistEngineToken, type ISurfaceAssistEngine } from './SurfaceAssistEngine';
import { buildAssistNarrative } from './SurfaceAssistNarration';
import type { AdHocAggregateResult, AdHocTrendResult, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';
import { ASSIST_WORKFLOWS, type AssistChartPlan, type AssistContext, type AssistPlan } from '@/shared/assist/AssistContract';

const AGGREGATE_LIMIT = 12;
const SESSION_KEY = 'ut-ama-session';

interface PersistedSession {
    promptText: string;
    plan: AssistPlan | null;
}

const WORKFLOW_VALUES = new Set<string>(ASSIST_WORKFLOWS);

function isPersistablePlan(plan: unknown): plan is AssistPlan {
    return typeof plan === 'object' && plan !== null && 'workflow' in plan && WORKFLOW_VALUES.has((plan as { workflow: unknown }).workflow as string);
}

function readSession(): PersistedSession {
    if (typeof window === 'undefined') return { promptText: '', plan: null };
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (raw === null) return { promptText: '', plan: null };
    try {
        const parsed = JSON.parse(raw) as Partial<PersistedSession>;
        return {
            promptText: typeof parsed.promptText === 'string' ? parsed.promptText : '',
            plan: isPersistablePlan(parsed.plan) ? parsed.plan : null,
        };
    } catch {
        return { promptText: '', plan: null };
    }
}

function writeSession(session: PersistedSession): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession(): void {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(SESSION_KEY);
}

/**
 * Orchestrates one assist interaction: routes the prompt to a plan via the
 * engine, runs any chart query through the existing analytics gateway (merging
 * the page's ambient filters), and grounds the narrative in the live result. The
 * "propose-field" plan is handed off to the classification builder by the view.
 */
@injectable()
export class SurfaceAssistViewModel extends BaseViewModel {
    promptText = '';
    loading = false;
    error: string | null = null;
    plan: AssistPlan | null = null;
    aggregate: AdHocAggregateResult | null = null;
    trend: AdHocTrendResult | null = null;
    narrative = '';

    constructor(
        @inject(ISurfaceAssistEngineToken) private engine: ISurfaceAssistEngine,
        @inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway,
    ) {
        super();
        const session = readSession();
        this.promptText = session.promptText;
        this.plan = session.plan;
        this.narrative = session.plan?.narrative ?? '';
    }

    get canAsk(): boolean {
        return this.promptText.trim().length > 0 && !this.loading;
    }

    /** True when a chart was restored from storage but its live data hasn't been re-fetched yet. */
    get needsRehydrate(): boolean {
        return this.plan?.workflow === 'chart' && this.aggregate === null && this.trend === null && !this.loading && this.error === null;
    }

    /** True when there is anything to clear — typed text or a held result. */
    get hasContent(): boolean {
        return this.promptText.length > 0 || this.plan !== null;
    }

    setPrompt(value: string): void {
        this.promptText = value;
        this.notifyChange();
    }

    async ask(context: AssistContext): Promise<void> {
        const prompt = this.promptText.trim();
        if (prompt.length === 0 || this.loading) return;
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            const plan = await this.engine.interpret({ prompt, context });
            this.plan = plan;
            this.aggregate = null;
            this.trend = null;
            this.narrative = plan.narrative;
            if (plan.workflow === 'chart') {
                await this.runChart(plan, context);
            }
        } catch (err) {
            this.error = err instanceof Error ? err.message : String(err);
            this.plan = null;
        } finally {
            this.loading = false;
            this.persistSession();
            this.notifyChange();
        }
    }

    /**
     * Re-runs the chart query for a plan restored from storage so the persisted
     * response comes back with fresh data after a reload. The plan's own filters
     * are dropped — they were ambient page state at ask-time (not persisted), so
     * the chart is re-scoped to the current live filters only. A no-op when there
     * is nothing to rehydrate.
     */
    async rehydrate(context: AssistContext): Promise<void> {
        if (!this.needsRehydrate) return;
        const plan = this.plan;
        if (plan === null || plan.workflow !== 'chart') return;
        this.loading = true;
        this.notifyChange();
        try {
            await this.runChart({ ...plan, filters: undefined }, context);
        } catch (err) {
            this.error = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    /** Clears the prompt text, the held result, and the persisted session. */
    reset(): void {
        this.promptText = '';
        this.plan = null;
        this.aggregate = null;
        this.trend = null;
        this.narrative = '';
        this.error = null;
        clearSession();
        this.notifyChange();
    }

    private persistSession(): void {
        writeSession({ promptText: this.promptText, plan: this.plan });
    }

    private async runChart(plan: AssistChartPlan, context: AssistContext): Promise<void> {
        const filters = [...context.activeFilters, ...(plan.filters ?? [])];
        if (plan.viz === 'trend') {
            this.trend = await this.gateway.trend({ source: 'interactions', bucket: plan.bucket ?? 'week', metrics: [plan.metric], filters });
            this.aggregate = null;
            this.narrative = plan.narrative;
            return;
        }
        const aggregate = await this.gateway.aggregate({
            source: 'interactions',
            groupBy: [plan.primaryField],
            metrics: chartMetrics(plan.metric),
            orderBy: { metric: plan.metric, direction: 'desc' },
            filters,
            limit: AGGREGATE_LIMIT,
        });
        this.aggregate = aggregate;
        this.trend = null;
        this.narrative = [plan.narrative, buildAssistNarrative(aggregate, plan.primaryField, plan.metric)].filter((part) => part.length > 0).join(' ');
    }
}

function chartMetrics(metric: AnalyticsMetric): AnalyticsMetric[] {
    return metric === 'users' ? ['users'] : [metric, 'users'];
}
